import React, { type ReactNode } from 'react';
import Button from '@atlaskit/button/standard-button';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Box, Text, xcss } from '@atlaskit/primitives';
import {
	AdControlDropdownMenu,
	DismissalButton,
	ReasonsButton,
} from '@atlassian/ad-control-toolkit';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	MODAL,
	MountEvent,
	fireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import messages from './messages.tsx';

const DATA_PRIVACY_LINK = 'https://www.atlassian.com/legal/privacy-policy';
const QUICK_MENU_TYPE = 'loom-only';

type Props = {
	onDismiss: () => Promise<void>;
	menuLocation?: string;
	bannerLocation?: string;
};

// AdControlProvider is needed when importing this component to provide the context for the AdControlDropdownMenu
const AdControls = ({ onDismiss, menuLocation, bannerLocation }: Props): React.JSX.Element => {
	const reasonsModalBulletTwoAppify = fg('product-terminology-refresh')
		? messages.reasonsModalBulletTwoAppify
		: messages.reasonsModalBulletTwo;
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const eventAttributes = {
		bannerLocation,
		menuLocation,
		isSiteAdmin,
		quickMenuType: menuLocation === 'quick-add' ? QUICK_MENU_TYPE : undefined,
	};

	const buttonClicked = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});

	const linkClicked = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'link',
	});

	return (
		<AdControlDropdownMenu
			itemId="loom-videos-banner-ad-controls"
			trigger={({ triggerRef, ...props }) => (
				<Box paddingInline="space.050">
					<Button
						{...props}
						appearance="subtle"
						spacing="compact"
						iconBefore={
							<MoreIcon label={formatMessage(messages.loomAdControlsTrigger)} spacing="spacious" />
						}
						ref={triggerRef}
						onClick={(event) => {
							props.onClick?.(event);
							fireUIAnalytics(buttonClicked, 'adControlsButton', eventAttributes);
						}}
					/>
				</Box>
			)}
		>
			{(props) => {
				return (
					<>
						<DismissalButton
							{...props}
							flagProps={{
								title: formatMessage(messages.dismissFlagTitle),
								description: formatMessage(messages.dismissFlagDescription),
							}}
							onClick={() => {
								fireUIAnalytics(buttonClicked, 'notInterestedButton', eventAttributes);
								return onDismiss();
							}}
						/>
						<ReasonsButton
							{...props}
							onClick={() => {
								fireUIAnalytics(buttonClicked, 'whyAmISeeingThisButton', eventAttributes);
							}}
							modalProps={{
								title: formatMessage(messages.reasonsModalTitle),
								body: (
									<ContextualAnalyticsData sourceName="whyAmISeeingThis" sourceType={MODAL}>
										<MountEvent
											onMount={(analyticsEvent: AnalyticsEvent) => {
												fireScreenAnalytics(analyticsEvent, eventAttributes);
											}}
										/>
										{formatMessage(messages.reasonsModalExplanation, {
											strong: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
										})}
										<ul>
											<li>{formatMessage(messages.reasonsModalBulletOne)}</li>
											<li>{formatMessage(reasonsModalBulletTwoAppify)}</li>
										</ul>
										<Box xcss={linkStyles}>
											<Button
												appearance="link"
												spacing="none"
												href={DATA_PRIVACY_LINK}
												target="_blank"
												onClick={() => {
													fireUIAnalytics(linkClicked, 'dataPrivacyLink');
												}}
											>
												{formatMessage(messages.privacyPolicy)}
											</Button>
										</Box>
									</ContextualAnalyticsData>
								),
							}}
						/>
					</>
				);
			}}
		</AdControlDropdownMenu>
	);
};

const linkStyles = xcss({
	marginTop: 'space.150',
});

export default AdControls;
